import { Backdrop, CircularProgress } from "@mui/material";
import { useStoryDetailQuery } from "Api/Queries/Stories/useStoryDetailQuery";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StoryViewer } from "Components/Stories/StoryViewer";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { toPreviewStoryGroup } from "Utils/Stories/storyPreviewGroup";

const RESOURCES = Resources.Stories.Management;

const noop = () => {};

type Props = {
  publicID: string;
  onClose: () => void;
};

/**
 * Plays the story in the SAME viewer customers get (timing, gestures, CTA, sheet), fed from our
 * record instead of Stream — so drafts and expired stories preview too. Completing the story or
 * closing starts the viewer's fade-out; the parent unmounts on onExited, like the stories bar.
 */
export const StoryPreviewDialog = ({ publicID, onClose }: Props) => {
  const { t } = useResource();
  const { data: detail, isLoading, isError } = useStoryDetailQuery(publicID);
  const [isClosing, setIsClosing] = useState(false);

  if (isLoading) {
    return (
      <Backdrop open onClick={onClose}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !detail) {
    return (
      <ModalDialog isOpen onClose={onClose} title={t(RESOURCES.Table.Preview)}>
        <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>
      </ModalDialog>
    );
  }

  const requestClose = () => setIsClosing(true);

  return (
    <StoryViewer
      storyGroup={toPreviewStoryGroup(detail)}
      isOpen
      isClosing={isClosing}
      onClose={requestClose}
      onComplete={requestClose}
      onPreviousSource={noop}
      onStoryViewed={noop}
      onExited={onClose}
    />
  );
};
